import { queryOptions } from '@tanstack/react-query'

import type { FeedbackListResponse } from '@/bindings/FeedbackListResponse'
import type { OrderFeedback } from '@/bindings/OrderFeedback'
import type { OrderFeedbackRequest } from '@/bindings/OrderFeedbackRequest'
import type { SendFeedbackRequest } from '@/bindings/SendFeedbackRequest'
import { publicPath } from '@/features/ordering/api'
import { request } from '@/lib/api'
import { type PageSearch, parsePageSearch } from '@/lib/page-search'
import { PAGE_SIZE } from '@/lib/paging'

/** Mirror of `romi_db::feedback::MAX_MESSAGE_CHARS`. */
export const MAX_MESSAGE_CHARS = 2000

/** `POST /feedback` (I3): logged in; to Romi, a restaurant, a branch, or one of the account's orders. */
export function sendFeedback(body: SendFeedbackRequest) {
  return request<OrderFeedback>('/feedback', { method: 'POST', body })
}

/** `POST /public/{r}/{b}/o/{code}/feedback`: the tracking page, anonymous or logged in. */
export function sendOrderFeedback(restaurantSlug: string, branchSlug: string, code: string, body: OrderFeedbackRequest) {
  return request<OrderFeedback>(publicPath(restaurantSlug, branchSlug, `/o/${encodeURIComponent(code)}/feedback`), { method: 'POST', body })
}

export interface FeedbackSearch extends PageSearch {
  branch?: string
}

export function parseFeedbackSearch(search: Record<string, unknown>): FeedbackSearch {
  const out: FeedbackSearch = parsePageSearch(search)
  if (typeof search.branch === 'string' && search.branch) out.branch = search.branch
  return out
}

export const feedbackKeys = {
  restaurantList: (id: string, branch: string | undefined, page: number) => ['feedback', 'restaurant', id, branch ?? '', page] as const,
  platformList: (page: number) => ['feedback', 'platform', page] as const,
}

export function restaurantFeedbackQuery(id: string, branch: string | undefined, page: number) {
  const params = new URLSearchParams({ page: String(page), per_page: String(PAGE_SIZE) })
  if (branch) params.set('branch_id', branch)
  return queryOptions({
    queryKey: feedbackKeys.restaurantList(id, branch, page),
    queryFn: () => request<FeedbackListResponse>(`/restaurants/${id}/feedback?${params}`),
  })
}

export function platformFeedbackQuery(page: number) {
  return queryOptions({
    queryKey: feedbackKeys.platformList(page),
    queryFn: () => request<FeedbackListResponse>(`/platform/feedback?page=${page}&per_page=${PAGE_SIZE}`),
  })
}
